import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { useApplicationConfig } from '~/data/applicationConfig';
import { useHasChanged } from '~/hooks/useHasChanged';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import {
  PasswordSubmitMethod,
  PasswordSubmitMethodFormFields,
} from '~/scenes/PeopleModule/components/EmployeeActions/ResetPassword/PasswordSubmissionMethodForm/data';
import { usePasswordSubmitMethods } from '~/scenes/PeopleModule/components/EmployeeActions/ResetPassword/PasswordSubmissionMethodForm/usePasswordSubmitMethods';
import { usePasswordSubmitMethodValidationSchema } from '~/scenes/PeopleModule/components/EmployeeActions/ResetPassword/PasswordSubmissionMethodForm/usePasswordSubmitMethodValidationSchema';

import { NewUserSendAccountDetails, sendUserAccountDetails } from '../../data';

export interface FormFields {
  username: string;
  password: string;
  passwordSubmit: PasswordSubmitMethodFormFields;
}

type PropsType = {
  newAccountDetails: { fLoginUsername?: string; fLoginPassword?: string };
  employeeId: number;
  onClose: () => void;
  workEmail: string;
};

export function useNewUserAccount(props: PropsType) {
  const { newAccountDetails, employeeId, onClose, workEmail } = props;

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { applicationConfig } = useApplicationConfig();
  const { isOnlyExternalSignInEnabled } = useMemo(() => {
    const isInternalSignInEnabled =
      applicationConfig?.fComConfig?.coreConfig?.fComInternalSignin ?? false;
    const isExternalSignInEnabled =
      applicationConfig?.fComConfig?.coreConfig?.fComExternalSignin ?? false;

    const isOnlyExternalSignInEnabled =
      !isInternalSignInEnabled && isExternalSignInEnabled;

    return {
      isOnlyExternalSignInEnabled,
    };
  }, [applicationConfig]);

  const { getPasswordSubmitMethods } = usePasswordSubmitMethods();

  const { isMethodsAvailable, defaultValues } = useMemo(() => {
    const { defaultMethod, isMethodEnabled, isMethodsAvailable } =
      getPasswordSubmitMethods(workEmail);

    const defaultValues: FormFields = {
      username: newAccountDetails?.fLoginUsername || '',
      password: newAccountDetails?.fLoginPassword || '',
      passwordSubmit: {
        method: defaultMethod,
        workEmail: workEmail ?? '',
        mobile: '',
        alternateEmail: '',
      },
    };

    return {
      defaultValues,
      defaultMethod,
      isMethodEnabled,
      isMethodsAvailable,
    };
  }, [getPasswordSubmitMethods, newAccountDetails, workEmail]);

  const yupPasswordSubmitSchema = usePasswordSubmitMethodValidationSchema();

  const validationSchema = yup.object().shape<FormFields>({
    username: yup.string().defined(),
    password: yup.string().defined(),
    passwordSubmit: yupPasswordSubmitSchema.defined(),
  });

  const resolver = useYupValidationResolver(
    validationSchema,
    'react-hook-form',
  );

  const form = useForm<FormFields>({
    resolver,
    defaultValues,
  });

  const { handleSubmit, reset } = form;

  const hasEmployeeChanged = useHasChanged(
    JSON.stringify({
      employeeId,
      workEmail,
      defaultValues,
    }),
  );
  useEffect(() => {
    if (hasEmployeeChanged) {
      reset(defaultValues);
    }
  }, [reset, defaultValues, hasEmployeeChanged]);

  const handleClose = useCallback(() => {
    reset(defaultValues);
    onClose();
  }, [onClose, reset, defaultValues]);

  const onSubmit = useMemo(() => {
    return handleSubmit((data) => {
      const {
        username: fLoginUsername,
        password: fLoginPassword,
        passwordSubmit: { method, mobile, alternateEmail },
      } = data;

      const resolvePayload = (): NewUserSendAccountDetails => {
        const common: Pick<
          NewUserSendAccountDetails,
          'fLoginEmpId' | 'fLoginUsername' | 'fLoginPassword'
        > = {
          fLoginEmpId: employeeId,
          fLoginUsername,
          fLoginPassword,
        };

        switch (method) {
          case PasswordSubmitMethod.WorkEmail:
            return common;
          case PasswordSubmitMethod.SMS:
            return {
              ...common,
              fSmsPhoneNumber: mobile,
            };
          case PasswordSubmitMethod.AlternateEmail:
            return {
              ...common,
              fAlternateEmail: alternateEmail,
            };
          default:
            return common;
        }
      };

      return sendUserAccountDetails(resolvePayload())
        .then(() => {
          enqueueSnackbar(
            isOnlyExternalSignInEnabled
              ? t('messageSentSuccessfully')
              : t('laUsernPassSendSuc'),
            {
              variant: 'success',
            },
          );
          onClose();
        })
        .catch(() => {
          enqueueSnackbar(t('laError'), {
            variant: 'error',
          });
        });
    });
  }, [
    handleSubmit,
    employeeId,
    enqueueSnackbar,
    onClose,
    t,
    isOnlyExternalSignInEnabled,
  ]);

  return { form, onSubmit, isMethodsAvailable, onClose: handleClose };
}
